import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import { Bold, Italic, Link as LinkIcon, List, ListOrdered } from 'lucide-react';

interface TipTapEditorProps {
  value: string;
  onChange: (value: string) => void;
}

// Template text in markdown format
const templateText = `# Animatronic Name
Write 1-2 sentences summarizing the animatronic's name, location, and when it opened.

## About
- Provide 2-3 sentences or bullet points about the character, its history, or role in the story it came from.
- If the animatronic is unique to the attraction and not part of a media brand, mention that here.

## Features
- Describe the movement of the animatronic, its appearance, audio, or lighting (if applicable).
- Include public information or strong rumors about technical specifications (cite sources).
- **Note:** Do not guess at technical specifications if no information exists.

## Role in the Attraction
- Summarize the role of the animatronic character in the attraction in 1-3 sentences.
- Explain the character's purpose in the story of the ride or show.

## Trivia (only if applicable)
- Add any cool trivia or interesting facts here.
- Could include history, creators, online hints, or details about the company that built the animatronic.
- Mention fan opinions, notable moments during opening/closure, or unique features.

## Sources
- Cite sources with embedded links.`;

const fontSizes = [
  { label: 'Small', value: '14px' },
  { label: 'Normal', value: '16px' },
  { label: 'Large', value: '20px' },
  { label: 'Extra Large', value: '24px' }
];

// Convert markdown to HTML
const markdownToHtml = (markdown: string): string => {
  return markdown
    // Convert headers
    .replace(/^# (.*$)/gm, '<h1>$1</h1>')
    .replace(/^## (.*$)/gm, '<h2>$1</h2>')
    // Convert bullet points
    .replace(/^- (.*$)/gm, '<li>$1</li>')
    .replace(/((?:<li>.*<\/li>\n?)+)/g, '<ul>$1</ul>')
    // Convert bold
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Convert italic
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    // Convert links
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')
    // Convert paragraphs
    .split('\n\n')
    .map(para => {
      if (!para.trim()) return '';
      if (!/^<[^>]+>/.test(para)) {
        return `<p>${para.trim()}</p>`;
      }
      return para;
    })
    .join('\n');
};

// Convert HTML to markdown
const htmlToMarkdown = (html: string): string => {
  return html
    .replace(/<h1[^>]*>([\s\S]*?)<\/h1>/g, '# $1\n\n')
    .replace(/<h2[^>]*>([\s\S]*?)<\/h2>/g, '## $1\n\n')
    .replace(/<ul[^>]*>([\s\S]*?)<\/ul>/g, '$1\n')
    .replace(/<li[^>]*>([\s\S]*?)<\/li>/g, '- $1\n')
    .replace(/<strong[^>]*>([\s\S]*?)<\/strong>/g, '**$1**')
    .replace(/<em[^>]*>([\s\S]*?)<\/em>/g, '*$1*')
    .replace(/<a[^>]*href="([^"]*)"[^>]*>([\s\S]*?)<\/a>/g, '[$2]($1)')
    .replace(/<p[^>]*>([\s\S]*?)<\/p>/g, '$1\n\n')
    .replace(/\n\s*\n\s*\n/g, '\n\n')
    .trim();
};

const TipTapEditor: React.FC<TipTapEditorProps> = ({ value, onChange }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-brand hover:text-brand-light underline'
        }
      }),
      TextStyle,
      Color,
      Placeholder.configure({
        placeholder: ''
      })
    ],
    content: value || markdownToHtml(templateText),
    onUpdate: ({ editor }) => {
      const markdown = htmlToMarkdown(editor.getHTML());
      onChange(markdown);
    }
  });

  // Update editor content when value changes
  React.useEffect(() => {
    if (editor && value && value !== htmlToMarkdown(editor.getHTML())) {
      editor.commands.setContent(markdownToHtml(value));
    }
  }, [editor, value]);

  if (!editor) {
    return null;
  }

  const toggleLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);

    if (url === null) {
      return;
    }

    if (url === '') {
      editor.chain().focus().unsetLink().run();
      return;
    }

    editor.chain().focus().setLink({ href: url }).run();
  };

  const setFontSize = (size: string) => {
    editor.chain().focus().setStyle({ fontSize: size }).run();
  };

  return (
    <div className="prose prose-invert max-w-none">
      <div className="flex items-center gap-2 mb-2 border-b border-gray-800 pb-2">
        {/* Font Size Selector */}
        <select
          onChange={(e) => setFontSize(e.target.value)}
          className="p-2 bg-background border border-gray-800 rounded text-sm text-content-secondary hover:text-content-primary"
          title="Font Size"
        >
          <option value="">Font Size</option>
          {fontSizes.map(size => (
            <option key={size.value} value={size.value}>
              {size.label}
            </option>
          ))}
        </select>

        <button
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`p-2 rounded hover:bg-background ${
            editor.isActive('bold') ? 'bg-brand/20 text-brand' : 'text-content-secondary'
          }`}
          title="Bold"
        >
          <Bold className="w-4 h-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`p-2 rounded hover:bg-background ${
            editor.isActive('italic') ? 'bg-brand/20 text-brand' : 'text-content-secondary'
          }`}
          title="Italic"
        >
          <Italic className="w-4 h-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`p-2 rounded hover:bg-background ${
            editor.isActive('bulletList') ? 'bg-brand/20 text-brand' : 'text-content-secondary'
          }`}
          title="Bullet List"
        >
          <List className="w-4 h-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`p-2 rounded hover:bg-background ${
            editor.isActive('orderedList') ? 'bg-brand/20 text-brand' : 'text-content-secondary'
          }`}
          title="Numbered List"
        >
          <ListOrdered className="w-4 h-4" />
        </button>
        <button
          onClick={toggleLink}
          className={`p-2 rounded hover:bg-background ${
            editor.isActive('link') ? 'bg-brand/20 text-brand' : 'text-content-secondary'
          }`}
          title="Add Link"
        >
          <LinkIcon className="w-4 h-4" />
        </button>
      </div>
      <EditorContent 
        editor={editor} 
        className="min-h-[200px] bg-background border border-gray-800 rounded-lg p-4 focus-within:border-brand focus-within:ring-2 focus-within:ring-brand/20"
      />
    </div>
  );
};

export default TipTapEditor;